import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { Application, ApiError, allApplicationsKey } from "./useGetAllApplications";

const jsonHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
};

type CreateApplicationData = {
    application: Omit<Application, "id" | "createdAt" | "updatedAt">;
};

const toApplicationFormError = (error: ApiError) => {
    if (error.response?.status === 422) {
        const validationErrors = error.response.data.violations;
        return validationErrors.reduce(
            (acc, violation) => ({
                ...acc,
                [violation.propertyPath]: violation.message,
            }),
            {},
        );
    }
    return new Error("An unexpected error occurred");
};

export const useCreateApplication = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation<Application, ApiError, CreateApplicationData>(
        async (options: CreateApplicationData) => {
            const { application } = options;
            try {
                const response = await loggedInApiCall.post("/api/integrations/applications", application, {
                    headers: jsonHeaders,
                });

                return response.data;
            } catch (error) {
                throw toApplicationFormError(error as ApiError);
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(allApplicationsKey);
            },
        },
    );

    return mutation;
};

export const useUpdateApplication = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation<
        Application,
        ApiError,
        { id: string; data: Partial<Omit<Application, "id" | "createdAt" | "updatedAt">> }
    >(
        async ({ id, data }) => {
            try {
                const response = await loggedInApiCall.put(`/api/integrations/applications/${id}`, data, {
                    headers: jsonHeaders,
                });
                return response.data;
            } catch (error) {
                throw toApplicationFormError(error as ApiError);
            }
        },
        {
            onSuccess: (updatedApplication) => {
                queryClient.invalidateQueries(allApplicationsKey);
                queryClient.invalidateQueries(["getOneApplication", updatedApplication.id]);
            },
        },
    );

    return mutation;
};
